//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

/**
 * The category for this event. Should be a top level feature name.
 */
export enum TRACKING_CATEGORY {
  ADS = 'InstagramAds',
  DYNAMIC_ADS_REPORTING = 'DynamicAdsReporting',
  BEAUTIFY = 'Beautify',
  BRAND_TAGGER = 'BrandTagger',
  BOOKMARKLET = 'Bookmarklet',
  CALENDAR = 'Calendar',
  CATEGORY_INFLUENCERS = 'CategoryInfluencers',
  CATEGORY_SETTINGS = 'CategorySettings',
  CONTENT_CARD = 'ContentCard',
  CONTENT_DETAIL = 'ContentSourceDetail',
  CROSS_POST = 'CrossPost',
  CUSTOMIZE_LIKE2BUY = 'CustomizeLike2Buy',
  EXPLORE = 'Explore',
  ECOMMERCE_INTEGRATIONS_MAGENTO = 'EcommerceIntegrationsMagento',
  ECOMMERCE_INTEGRATIONS_SHOPIFY = 'EcommerceIntegrationsShopify',
  EMAIL_BUILDER = 'EmailBuilder',
  FACEBOOK_DASHBOARD = 'FacebookDashboard',
  FANREEL_CUSTOMIZATIONS = 'FanreelCustomizations',
  FANREEL_ECOMM_INTEGRATION = 'FanreelEcommIntegration',
  FILTER_SET = 'FilterSet',
  GALLERIES = 'Galleries',
  GOOGLE_ASSET_FEEDS = 'GoogleAssetFeeds',
  HOMEPAGE = 'Homepage',
  INFLUENCERS = 'Influencers',
  INFO_ALERT = 'InfoAlert',
  INSIGHTS = 'Insights',
  INSTAGRAM_ACCOUNT = 'InstagramAccount',
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS = 'InstagramAccountEngagementPosts',
  INSTAGRAM_AUDIENCE = 'InstagramAudience',
  INSTAGRAM_INTERACTIVE_CALENDAR = 'InstagramInteractiveCalendar',
  INSTAGRAM_COMPETITORS = 'InstagramCompetitors',
  INSTAGRAM_FEED_PREVIEW = 'InstagramFeedPreview',
  INSTAGRAM_POST_DETAILS = 'InstagramPostDetails',
  INSTAGRAM_STORIES = 'InstagramStories',
  LIBRARY = 'Library',
  LIKE2BUY_MODERATION = 'Like2buyModeration',
  MULTIPRODUCT = 'Multiproduct',
  NAVIGATION3 = 'Navigation3',
  NOTIFICATIONS = 'Notifications',
  ONE_PAGE = 'OnePage',
  ONBOARDING = 'Onboarding',
  PAGE_LOAD = 'PageLoad',
  PINTEREST_POST_DETAILS = 'PinterestPostDetails',
  PINTEREST_SUMMARY = 'PinterestSummary',
  PROMOTED_POSTS = 'PromotedPosts',
  PRODUCT_CATALOG_HEALTH = 'ProductCatalogHealth',
  REQUEST_ERROR = 'RequestError',
  REVEAL = 'RevealPage',
  RIGHTS_CONFIGURATION = 'RightsConfiguration',
  SCHEDULER = 'BulkScheduler', // DO NOT CHANGE = Using for consistency with old tracking data.
  SETTINGS = 'SettingsPage',
  SHOP_TAGS = 'ShopTags',
  SHOPIFY_ONBOARDING = 'ShopifyOnboarding',
  SHOWROOM_ANALYTICS_OVERVIEW = 'ShowroomAnalyticsOverview',
  SHOWROOM_ANALYTICS_POSTS = 'ShowroomAnalyticsPosts',
  SHOWROOM_ANALYTICS_ACTIVITY = 'ShowroomAnalyticsActivity',
  SIGNUP = 'Signup',
  SYN = 'Syndication',
  STREAMS = 'Streams',
  TAGGER = 'Tagger',
  TEAM_MANAGEMENT = 'TeamManagement',
  TIKTOK_SUMMARY = 'TikTokSummary',
  TRACKED_SOURCE_MANAGEMENT = 'TrackedSourceManagement',
  UAM = 'UniversalActionModal',
  UNIVERSAL_IMAGE_ACTIONS = 'UniversalImageActions',
  BULK_UIA = 'BulkUia',
  UNIVERSAL_UPLOADER = 'UniversalUploader',
  UNIVERSAL_SOURCE_ACTIONS = 'UniversalSourceActions',
  WELCOME_SCREEN = 'WelcomeScreen',
  DROPDOWN_WITH_ON_BRAND = 'DropdownWithOnBrand',
  REPORT_EXPORT_PAGE = 'ReportExportPage',

  /**
   * Assorted additions to the enum, were previously just
   * passed in as raw strings throughout frontend code.
   */
  HOMEPAGE_FEATURES_SPOTLIGHT = 'homepage-features-spotlight',

  /**
   * These tracking categories were previously manually specified as the
   * insights-tracking-constants, hence the difference in casing.
   */
  SIDEBAR = 'insights-sidebar',
  SUMMARY_PAGE = 'insights-summary-analytics',
  SUMMARY_MEDIA_GRID = 'insights-summary-media-grid',
  SUMMARY_PRODUCT_GRID = 'insights-summary-product-grid',
  SUMMARY_TOUCHPOINT_TABLE = 'insights-summary-touchpoint-table',
  LIKE2BUY_PAGE = 'insights-like2buy-analytics',
  LIKE2BUY_MEDIA_GRID = 'insights-like2buy-media-grid',
  LIKE2BUY_MEDIA_TABLE = 'insights-like2buy-media-table',
  LIKE2BUY_PRODUCT_GRID = 'insights-like2buy-product-grid',
  LIKE2BUY_PRODUCT_TABLE = 'insights-like2buy-product-table',
  FANREEL_PAGE = 'insights-fanreel-analytics',
  FANREEL_MEDIA_GRID = 'insights-fanreel-media-grid',
  FANREEL_MEDIA_TABLE = 'insights-fanreel-media-table',
  FANREEL_CONTRIBUTOR_GRID = 'insights-fanreel-contributor-grid',
  FANREEL_CONTRIBUTOR_TABLE = 'insights-fanreel-contributor-table',
  FANREEL_PRODUCT_GRID = 'insights-fanreel-product-grid',
  FANREEL_PRODUCT_TABLE = 'insights-fanreel-product-table',
  SYNDICATION_PAGE = 'insights-syndication',
  BUBBLE_STATS = 'insights-bubble-stats',
}

/**
 * The action this event represents. Should reflect the action the user is taking
 * in the UI, such as dragging an item or opening a link.
 */
export enum TRACKING_ACTION {
  ACTION_EMPLOYEE_ELEVATION_DISABLED = 'ActionEmployeeElevationDisabled',
  ACTION_EMPLOYEE_ELEVATION_ENABLED = 'ActionEmployeeElevationEnabled',
  ACTION_GENERAL_PASSWORD_CHANGE = 'ActionGeneralPasswordChange',
  ACTION_GENERAL_UPDATE_TIMEZONE = 'ActionGeneralUpdateTimezone',
  ACTION_INFLUENCERS_BULK_ADD = 'ActionInfluencersBulkAdd',
  ACTION_INFLUENCERS_DETAILS_CONFIGURE_RIGHTS = 'ActionInfluencersDetailsConfigureRights',
  ACTION_INFLUENCERS_DETAILS_EXPORT_USER = 'ActionInfluencersDetailsExportUser',
  ACTION_INFLUENCERS_LIST_CONFIGURE_RIGHTS = 'ActionInfluencersListConfigureRights',
  ACTION_INFLUENCERS_LIST_OPEN_RIGHTS = 'ActionInfluencersListOpenRights',
  ACTION_INFLUENCERS_DELETE_LIST = 'ActionInfluencersDeleteList',
  ACTION_INFLUENCERS_EXPORT_LIST = 'ActionInfluencersExportList',
  ACTION_INFLUENCERS_OPEN_BULK_ADD = 'ActionInfluencersOpenBulkAdd',
  ADD_FILTER = 'AddFilter',
  ADD_NOTE = 'AddNote',
  ADD_PRODUCT = 'AddProduct',
  ADD_TAG = 'AddTag',
  ADD_TO_LIBRARY = 'AddToLibrary',
  APPROVE_FOR_FANREEL = 'ApproveForFanreel',
  CALENDAR_MONTH_VIEW_ITEM_PICKUP = 'CalendarMonthViewItemPickup',
  CALENDAR_WEEK_VIEW_ITEM_PICKUP = 'CalendarWeekViewItemPickup',
  CALENDAR_MONTH_VIEW_ITEM_DROP = 'CalendarMonthViewItemDrop',
  CALENDAR_WEEK_VIEW_ITEM_DROP = 'CalendarWeekViewItemDrop',
  CANCEL = 'Cancel',
  CANCEL_PRODUCT_EDIT = 'CancelProductEdit',
  CANCEL_QUEUED_RIGHTS_REQUEST = 'CancelQueuedRightsRequest',
  CHANGE_VIEW = 'ChangeView',
  CHOOSE_MESSAGE = 'ChooseMessageDropdown',
  CLOSE = 'Close',
  CLOSE_MODAL = 'ModalClose',
  CREATE_PROMOTION = 'CreatePromotion',
  DELETE = 'Delete',
  DELETE_PROMOTION = 'DeletePromotion',
  DOWNLOAD = 'Download',
  DOWNLOAD_PDF_REPORT = 'DownloadPdfReport',
  DOWNLOAD_COMMENT_EXPORT = 'DownloadCommentExport',
  DOWNLOAD_LEGACY = 'DownloadLegacy',
  DRAG_SORT = 'DragSort',
  EDIT = 'Edit',
  EMAIL = 'Email',
  ERROR = 'Error',
  EXPORT = 'Export',
  EXPORT_COMMENTS = 'ExportComments',
  LOAD_FACEBOOK_SDK = 'LoadFacebookSdk',
  HIDE = 'Hide',
  LAUNCH = 'Launch',
  LIKE = 'Like',
  LOAD = 'Load',
  MISC = 'Misc',
  MODAL = 'Modal',
  MODERATE = 'Moderate',
  MODERATE_TIMEDELTA = 'ModerateDelta',
  ONE_PAGE = 'OnePage',
  OPEN = 'Open',
  OPEN_MODAL = 'ModalOpen',
  OPEN_REPORT_MODAL = 'ReportModalOpen',
  OPEN_DRAWER = 'OpenDrawer',
  OPEN_PREVIEW = 'OpenPreview',
  PAUSE_PROMOTION = 'PausePromotion',
  RESUME_PROMOTION = 'ResumePromotion',
  UPDATE_INSIGHTS_FOR_POST = 'UpdateInsightsForPost',
  PLAY_VIDEO = 'PlayVideo',
  PRODUCT_SEARCH = 'ProductSearch',
  REMOVE_FILTER = 'RemoveFilter',
  REMOVE_PRODUCT = 'RemoveProduct',
  REMOVE_TAG = 'RemoveTag',
  REQUESTED_RIGHTS = 'RequestedRights',
  REVEAL = 'Reveal',
  SAVE = 'Save',
  SCHEDULE_IMAGE = 'ScheduleImage',
  SEARCH = 'Search',
  SEARCH_ERROR = 'SearchError',
  SELECT_DROPDOWN = 'SelectDropdown',
  SELECT_TOGGLE = 'SelectToggle',
  SET_FILTER = 'SetFilter',
  SHOW_LESS = 'ShowLess',
  SHOW_MORE = 'ShowMore',
  SPOTLIGHT_LINK = 'SpotlightLink',
  TRACK_HASHTAG = 'TrackHashtag',
  TRACK_LOCATION = 'TrackLocation',
  TRIBUTARY = 'Tributary',
  TRIBUTARY_CREATE = 'TributaryCreate',
  TRIBUTARY_UPDATE = 'TributaryUpdate',
  UNTRACK_HASHTAG = 'UntrackHashtag',
  UNTRACK_LOCATION = 'UntrackLocation',
  UNLIKE = 'Unlike',
  UPDATE_FILTER = 'UpdateFilter',
  UPLOAD = 'Upload',
  UPLOAD_BANNER = 'UploadBanner',
  UPLOAD_ERROR = 'UploadError',
  VIEW_ALL = 'ViewAll',
  VIEW_ORIGINAL = 'ViewOriginal',
  VIEW_PANEL = 'ViewPanel',

  /* Navigation3 */
  NAVIGATION3_TOP_NAV_HOVER = 'TopNavHover',
  NAVIGATION3_TOP_NAV_PAGE_CLICK = 'TopNavPageClick',
  NAVIGATION3_TOP_NAV_HELP = 'TopNavHelp',
  NAVIGATION3_SETTINGS_HOVER = 'SettingsHover',
  NAVIGATION3_SETTINGS_DROPDOWN = 'SettingsDropdown',
  NAVIGATION3_BRAND_CHANGE = 'SettingsDropdownBrandChange',
  NAVIGATION3_WELCOME_SCREEN_PAGE_CLICK = 'WelcomeScreenPageClick',
  NAVIGATION3_WELCOME_SCREEN_RESOURCES_CLICK = 'WelcomeScreenResourceClick',
  NAVIGATION3_WELCOME_SCREEN_SPLASH_CLICK = 'WelcomeScreenSplashClick',
  NAVIGATION3_UPSELL_MODAL = 'UpsellModal',

  /* Shopify Onboarding */
  ONBOARDING_VIEW = 'OnboardingView',
  L2B_GET_STARTED_CLICK = 'L2BGetStartedClick',
  L2B_HOW_TO_CLICK = 'L2BHowToClick',
  TRACKING_PIXEL_HOW_TO_CLICK = 'TrackingPixelHowToClick',
  TRACKING_SUPPORT_LEARN_MORE_CLICK = 'SupportLearnMoreClick',

  /* UIA - Productize specific */
  ADD_PRODUCT_SPATIAL = 'AddProductBySpatial',
  ADD_PRODUCT_SEARCH = 'AddProductBySearch',
  PRODUCTIZE = 'Productize',
  RATE_PRODUCT_SUGGESTIONS = 'RateProductSuggestions',
  ADD_PRODUCT_BOUNDING_BOX = 'AddProductBoundingBox',
  SELECT_PRODUCT_SUGGESTION = 'SelectProductSuggestion',
  DELETE_PRODUCT_BOUNDING_BOX = 'DeleteProductBoundingBox',

  /* SYNDICATION */
  INVITATION = 'Invitation',
  SEND_TO_FANREEL = 'SendToFanreel',
  ADD_PRODUCT_TAGS = 'AddProductTags',
  INSTAGRAM_CONNECT = 'ConnectWithInstagram',
  PASSWORD = 'Password',
  USER = 'UserManagement',

  /* ADS */
  CAMPAIGN_LEVEL_METRICS = 'CampaignLevelMetrics',
  AD_LEVEL_METRICS = 'AdLevelMetrics',
  AD_MANAGER_CLICK = 'AdManagerClick',
  AD_CREATION_STEP = 'AdCreationStep',
  AD_CREATE = 'AdCreate',

  /* INFLUENCERS */
  EXPLORE_CREATE_SAVED_LIST = 'ExploreCreateSavedList',
  EXPLORE_DELETE_SAVED_LIST = 'ExploreDeleteSavedList',
  EXPLORE_CLICK_USER = 'ClickUser',
  EXPLORE_SAVE_USER = 'SaveUser',
  EXPLORE_REMOVE_SAVED_USER = 'RemoveSavedUser',
  EXPLORE_HIDE_USER = 'HideUser',
  EXPLORE_CLICK_MEDIA = 'ClickMedia',

  /* INFLUENCERS V2 */
  INFLUENCERS_SEARCH = 'SearchInfluencers',
  INFLUENCERS_ADD_TO_LIST = 'AddToList',
  INFLUENCERS_CREATE_LIST = 'CreateList',
  INFLUENCERS_OPEN_BULK_ADD = 'OpenBulkAdd',
  INFLUENCERS_BULK_ADD = 'BulkAddToList',
  INFLUENCERS_DELETE_INFLUENCER = 'DeleteInfluencer',
  INFLUENCERS_DELETE_LIST = 'DeleteList',
  INFLUENCERS_EXPORT_LIST = 'ExportList',
  INFLUENCERS_TOGGLE_LIST_MEDIA_VIEW = 'ToggleListMediaView',
  INFLUENCERS_TOGGLE_TABLE_GRID = 'ToggleTableGridView',
  INFLUENCERS_SEARCH_LIST = 'SearchList',
  INFLUENCERS_SORT_LIST = 'SortList',
  INFLUENCERS_DETAILS_VIEW_ON_INSTAGRAM = 'ViewUserOnInstagram',
  INFLUENCERS_DETAILS_EMAIL_USER = 'EmailUser',
  INFLUENCERS_DETAILS_EXPORT_USER = 'ExportUser',
  INFLUENCERS_DETAILS_TRACK_USER = 'TrackUser',
  INFLUENCERS_DETAILS_UNTRACK_USER = 'UntrackUser',
  INFLUENCERS_DETAILS_OPEN_RIGHTS = 'OpenRightsModalForUser',
  INFLUENCERS_DETAILS_CONFIGURE_RIGHTS = 'ConfigureRightsForUser',
  INFLUENCERS_LIST_OPEN_RIGHTS = 'OpenRightsModalForList',
  INFLUENCERS_LIST_CONFIGURE_RIGHTS = 'ConfigureRightsForList',

  /* TRACKED SOURCES */
  EXPLORE_TRACK_SOURCE = 'TrackSource',
  EXPLORE_UNTRACK_SOURCE = 'UntrackSource',

  /* NOTIFICATIONS */
  CLICK = 'ActionClick',
  UPDATE_PREFERENCE = 'UpdatePreference',
  DISMISS = 'Dismiss',

  /* SETTINGS PAGE */
  CONNECT_ACCOUNT = 'ConnectAccount',
  DISCONNECT_ACCOUNT = 'DisconnectAccount',
  CONNECT_ADS_ACCOUNT = 'ConnectAdsAccount',
  DISCONNECT_ADS_ACCOUNT = 'DisconnectAdsAccount',
  GA_SWITCH_PROFILE = 'GA:SwitchProfile',

  /* LIBRARY */
  COLLECTIONS_ABANDON_CHANGES = 'CollectionAbandonChanges',
  COLLECTIONS_ADD_ITEM = 'CollectionAddItem',
  COLLECTIONS_REMOVE_ITEM = 'CollectionRemoveItem',
  COLLECTIONS_ADD_TRACKED_SOURCE = 'CollectionAddTrackedSource',
  COLLECTIONS_CREATE_COLLECTION = 'CreateNewCollection',
  COLLECTIONS_DELETE_COLLECTION = 'DeleteCollection',
  COLLECTIONS_TOGGLE_SPAM = 'CollectionToggleSpam',
  COLLECTIONS_TOGGLE_VIEW = 'CollectionToggleView',
  COLLECTIONS_VIEW_ADVANCED_SETTINGS = 'CollectionViewAdvancedSettings',
  COLLECTIONS_UPDATE = 'CollectionUpdate',
  COLLECTIONS_SAVE_AS = 'CollectionSaveAs',
  COLLECTIONS_SEARCH_SOURCES = 'CollectionSearchSources',
  COLLECTIONS_SELECT_SOURCE = 'CollectionSelectSource',
  COLLECTIONS_DESELECT_SOURCE = 'CollectionDeselectSource',
  COLLECTIONS_SOURCES_VIEW_ALL = 'CollectionSourcesViewAll',
  LIBRARY_OPEN_FILTERS = 'OpenFilters',
  LIBRARY_SAVE_DEFAULT_VIEW = 'SaveDefaultView',

  /* FANREEL_MANAGEMENT */
  COPY_SNIPPET = 'CopySnippet',
  SETUP_LEARN_MORE = 'SetupLearnMore',

  /* FANREEL_ECOMM_INTEGRATION */
  DEPLOY_ECOMM_INTEGRATION = 'DeployEcommIntegration',
  REMOVE_ECOMM_INTEGRATION = 'RemoveEcommIntegration',

  /* FANREEL_CUSTOMIZATIONS */
  CUSTOMIZATION_CHANGED = 'CustomizationChanged',
  INSTRUCTIONS_MAILTO_CLICKED = 'CustomizationsSetupInstructionsMailtoClicked',

  /* GALLERIES */
  GALLERIES_CREATE_GALLERY_CTA_CLICKED = 'GalleriesCreateGalleryCtaClicked',
  GALLERIES_CREATE_GALLERY = 'GalleriesCreateGallery',
  GALLERIES_CUSTOMIZE_GALLERY_CTA_CLICKED = 'GalleriesCustomizeGalleryCtaClicked',
  GALLERIES_EMBED_GALLERY_CTA_CLICKED = 'GalleriesEmbedGalleryCtaClicked',
  GALLERIES_MEDIA_FILTER_SELECTED = 'GalleriesMediaFilterSelected',
  GALLERIES_ADD_ITEMS_TO_GALLERIES = 'GalleriesAddItemsToGalleries',
  GALLERIES_ITEM_REQUEST_RIGHTS_CTA_CLICKED = 'GalleriesItemRequestRightsCtaClicked',
  GALLERIES_ITEM_TAG_PRODUCT_CTA_CLICKED = 'GalleriesItemTagProductCtaClicked',
  GALLERIES_GALLERY_PREVIEW = 'GalleryPreview',

  /* PINNED MEDIA FOR GALLERIES */
  PINNED_MEDIA_PIN_ITEM = 'MediaPin',
  PINNED_MEDIA_UNPIN_ITEM = 'MediaUnPin',
  PINNED_MEDIA_SHOW_PINNED_MEDIA = 'ShowPinnedMedia',
  PINNED_MEDIA_HIDE_PINNED_MEDIA = 'HidePinnedMedia',
  PINNED_MEDIA_FILTER_PINNED_MEDIA = 'FilterPinnedMedia',
  PINNED_MEDIA_DRAG_START = 'ItemDragStart',
  PINNED_MEDIA_DRAG_END = 'ItemDragEnd',
  PINNED_MEDIA_GALLERY_PREVIEW_SHOW = 'ShowPinnedMediaPreview',
  PINNED_MEDIA_GALLERY_PREVIEW_HIDE = 'HidePinnedMediaPreview',
  PINNED_MEDIA_GALLERY_PREVIEW_DRAG_START = 'ItemDragStartPreview',
  PINNED_MEDIA_GALLERY_PREVIEW_DRAG_END = 'ItemDragEndPreview',

  /* STREAMS */
  STREAMS_NEW_STREAM_CTA_CLICKED = 'StreamsNewStreamCtaClicked',
  STREAMS_CREATE_STREAM = 'StreamsCreateStream',
  STREAMS_EDIT_STREAM = 'StreamsEditStream',
  STREAMS_REMOVE_ITEMS_FROM_STREAM = 'StreamsRemoveItemsFromStream',
  STREAMS_SELECT_STREAM_FROM_SIDEBAR = 'StreamsSelectStreamFromSidebar',
  STREAMS_SELECT_STREAM_FROM_BUTTON = 'StreamsSelectStreamFromButton',
  STREAMS_SHARE_IMAGE_TO_GALLERY_FROM_STREAM = 'StreamsShareImageToGalleryFromStream',
  STREAMS_SHARE_IMAGE_TO_GALLERY_FROM_SUMMARY = 'StreamsShareImageToGalleryFromSummary',
  STREAMS_IMAGE_CLICKED_FROM_STREAM = 'StreamsImageClickedFromStream',
  STREAMS_IMAGE_CLICKED_FROM_SUMMARY = 'StreamsImageClickedFromSummary',
  STREAMS_SUMMARY_LOADED = 'StreamsSummaryLoaded',
  STREAMS_CAROUSEL_VIEWED = 'StreamsCarouselViewed',
  STREAMS_LEFT_ARROW_CLICKED = 'StreamsLeftArrowClicked',
  STREAMS_RIGHT_ARROW_CLICKED = 'StreamsRightArrowClicked',

  /* PRODUCT_FEED_HEALTH */
  CATALOG_HEALTH_FAQ_BUTTON_CLICKED = 'CatalogHealthFaqButtonClicked',
  CATALOG_HEALTH_SWITCH_CATALOG_VIEW = 'CatalogHealthSwitchCatalogView',
  CATALOG_HEALTH_EXPORT_ERRORS_BUTTON_CLICKED = 'CatalogHealthExportErrorsButtonClicked',

  /* ECOMMERCE_INTEGRATIONS_SHOPIFY & ECOMMERCE_INTEGRATIONS_MAGENTO */
  ECOMM_INTEGRATIONS_GET_STARTED_BUTTON_CLICKED = 'EcommIntergrationsGetStartedButtonClicked',
  ECOMM_INTEGRATIONS_CATALOG_FAQ_LINK_CLICKED = 'EcommIntegrationsCatalogFaqLinkClicked',
  ECOMM_INTEGRATIONS_GO_BACK_BUTTON_CLICKED = 'EcommIntegrationsGoBackButtonClicked',
  ECOMM_INTEGRATIONS_QUIT_BUTTON_CLICKED = 'EcommIntegrationsQuitButtonClicked',
  ECOMM_INTEGRATIONS_SAVE_CHANGES_BUTTON_CLICKED = 'EcommIntegrationsSaveChangesButtonClicked',

  /* INSTAGRAM COMPETITORS */
  EXPORT_COMPETITORS_CSV = 'ExportCompetitorsCsv',

  /* INSTAGRAM POST DETAILS */
  REORDER_MEDIA_DRAG = 'ReorderMediaDrag',
  REORDER_STORY_CLIP_DRAG = 'ReorderStoryClipDrag',

  /* INSTAGRAM STORIES */
  STORIES_LEARN_MORE = 'EmptyStateLearnMore',
  STORIES_RECONNECT_ACCOUNT = 'ReconnectAccount',

  /* TRACK BUTTON */
  UNTRACK_CONFIRMED = 'UntrackConfirmed',

  /* INSTAGRAM ACCOUNT SIDEBAR*/
  TRACK_NEW_HASHTAG = 'TrackNewHashtag',

  /* SIGNUP */
  SIGNUP_INFO_CHANGED = 'SignupInfoChanged',
  SIGNUP_PAYMENT_CHANGED = 'SignupPaymentChanged',
  SIGNUP_SUCCESS = 'SignupSuccess',

  /* SCHEDULER */
  SCHEDULER_ADD_LOCATION = 'SchedulerAddLocation',
  SCHEDULER_REMOVE_LOCATION = 'SchedulerRemoveLocation',
  SCHEDULER_ADD_USER = 'SchedulerAddUser',
  SCHEDULER_REMOVE_USER = 'SchedulerRemoveUser',

  /* Promoted Posts */
  OPENED_PROMOTED_POSTS_WORKFLOW = 'OpenedPromotedPostsWorkflow',
  LOADED_ACCOUNT_PANEL = 'LoadedAccountPanel',
  LOADED_AUDIENCE_PANEL = 'LoadedAudiencePanel',
  LOADED_BUDGET_PANEL = 'LoadedBudgetPanel',
  LOADED_TARGET_PANEL = 'LoadedTargetPanel',
  LOADED_SUMMARY_PANEL = 'LoadedSummaryPanel',
  CHANGED_AD_ACCOUNT = 'ChangedAdAccount',
  CHANGED_CUSTOM_LINK = 'ChangedCustomLink',
  CHANGED_SELECTED_TARGET = 'ChangedSelectedTarget',
  CHANGED_CTA = 'ChangedCTA',
  CHANGED_BUDGET = 'ChangedBudget',
  CHANGED_DURATION = 'ChangedDuration',
  CHANGED_SELECTED_AUDIENCE = 'ChangedSelectedAudience',
  CHANGED_LOOKALIKE_AUDIENCE = 'ChangedLookalikeAudience',
  CHANGED_AUDIENCE_LOCATION = 'ChangedAudienceLocation',
  CHANGED_AUDIENCE_AGE = 'ChangedAudienceAge',
  CHANGED_AUDIENCE_GENDERS = 'ChangedAudienceGenders',
  EDIT_SHOWROOM_LINK = 'EditShowroomLink',
  PREVIEW_SHOWROOM_LINK = 'PreviewShowroomLink',
  OPEN_PREVIEW_FROM_POST = 'OpenPreviewFromPost',
  OPEN_HELP_LINK = 'OpenHelpLink',
  CANCELED_WORKFLOW = 'CanceledWorkflow',
  OPEN_PROMOTED_POSTS_METRICS_PAGE = 'OpenPromotedPostsMetricsPage',
  CSV_EXPORT = 'CSVExport',
  SORT_TABLE = 'SortTable',

  /* Bulk UIA */
  BULK_UIA_SELECT = 'BulkUiaSelect',
  BULK_UIA_DESELECT = 'BulkUiaDeselect',
  BULK_UIA_RANGE_SELECT = 'BulkUiaRangeSelect',
  BULK_UIA_RANGE_DESELECT = 'BulkUiaRangeDeselect',
  BULK_UIA_OPEN = 'BulkUiaOpen',
  BULK_UIA_ADD_LABELS = 'BulkUiaAddLabels',
  BULK_UIA_REMOVE_LABELS = 'BulkUiaRemoveLabels',
  BULK_UIA_ADD_TO_LIBRARY = 'BulkUiaAddToLibrary',
  BULK_UIA_REMOVE_FROM_LIBRARY = 'BulkUiaRemoveFromLibrary',
  BULK_UIA_ADD_TO_COLLECTION = 'BulkUiaAddToCollection',
  BULK_UIA_ADD_TO_GALLERY = 'BulkUiaAddToGallery',
  BULK_UIA_REMOVE_FROM_COLLECTION = 'BulkUiaRemoveFromCollection',
  BULK_UIA_REMOVE_FROM_CONNECT = 'BulkUiaRemoveFromConnect',
  BULK_UIA_REMOVE_FROM_GALLERY = 'BulkUiaRemoveFromGallery',
  BULK_UIA_REMOVE_FROM_GOOGLE_ASSET_FEED = 'BulkUiaRemoveFromGoogleAssetFeed',
  BULK_UIA_PUBLISH_TO_GOOGLE_ASSET_FEED = 'BulkUiaPublishToGoogleAssetFeed',
  BULK_UIA_REQUEST_REVIEW_NOW = 'BulkUiaRequestReviewNow',
  BULK_UIA_DOWNLOAD = 'BulkUiaDownload',

  /* Beautify */
  BEAUTIFY_OPEN_EDITOR = 'BeautifyOpenEditor',
  BEAUTIFY_CLOSE_EDITOR = 'BeautifyCloseEditor',
  BEAUTIFY_SAVE_IMAGE = 'BeautifySaveImage',
  BEAUTIFY_UNDO_EDITS = 'BeautifyUndoEdits',

  /* Shared Dashboard */
  EXPORT_AUDIENCE_CSV = 'ExportAudienceCSV',

  /* Facebook Dashboard */
  AUDIENCE_DATE_PICKER = 'AudienceDatePicker',
  CALENDAR_VIEW_DAY = 'CalendarViewDay',
  CALENDAR_VIEW_WEEK = 'CalendarViewWeek',
  CALENDAR_VIEW_MONTH = 'CalendarViewMonth',
  CALENDAR_VIEW_UNKNOWN = 'CalendarViewUnknown',
  COMPLETE_ONBOARDING_ONE = 'CompleteOnboardingOne',
  COMPLETE_ONBOARDING_TWO = 'CompleteOnboardingTwo',
  COMPLETE_ONBOARDING_THREE = 'CompleteOnboardingThree',
  DELETE_POST = 'DeletePost',
  EDIT_POST = 'EditPost',
  ENGAGEMENT_DATE_PICKER = 'EngagementDatePicker',
  ENGAGEMENT_SORT = 'EngagementSort',
  EXPORT_ENGAGEMENT_CSV = 'ExportEngagementCSV',
  SCHEDULE_GALLERY = 'ScheduleGallery',
  SCHEDULE_LINK = 'ScheduleLink',
  SCHEDULE_PHOTO = 'SchedulePhoto',
  SCHEDULE_VIDEO = 'ScheduleVideo',
  TOGGLE_ENGAGEMENT = 'ToggleEngagement',
  TOGGLE_SUMMARY_CHART = 'ToggleSummaryChart',
  ONBOARDING_ERROR = 'OnboardingError',
  POST_ONBOARDING_ERROR = 'PostOnboardingError',

  /* Email Builder */
  EMAIL_BUILDER_COPY_HTML_EVERGREEN = 'CopyHtmlEvergreen',
  EMAIL_BUILDER_COPY_HTML_CAMPAIGN = 'CopyHtmlCampaign',
  EMAIL_BUILDER_SET_FILTER_EVERGREEN = 'SetFilterEvergreen',
  EMAIL_BUILDER_SET_FILTER_CAMPAIGN = 'SetFilterCampaign',
  EMAIL_BUILDER_SET_LAYOUT_EVERGREEN = 'SetLayoutEvergreen',
  EMAIL_BUILDER_SET_LAYOUT_CAMPAIGN = 'SetLayouCampaign',
  EMAIL_BUILDER_SET_SOCIAL = 'SetSocial',
  EMAIL_BUILDER_SET_OPTION = 'SetOption',

  /* Recent Engagers */
  RECENT_ENGAGER_CLICK_TRACKED_USER = 'RecentEngagerClickTrackedUser',
  RECENT_ENGAGER_CLICK_NOT_TRACKED_USER = 'RecentEngagerClickNotTrackedUser',

  /* Google Asset Feeds */
  GOOGLE_ASSET_FEED_PUBLISH_ALL = 'GoogleAssetFeedPublishAll',

  /* ONE Page */
  ONE_PAGE_COPY_LINK = 'CopyOnePageLink',
  ONE_PAGE_PREVIEW_LINK = 'PreviewOnePageLink',
  ONE_PAGE_GENERATE_LINK = 'GenerateOnePageLink',
  ONE_PAGE_PRELOAD_LINK = 'PreloadOnePageLink',

  /* Most on brand sort */
  DROPDOWN_WITH_ON_BRAND_SORT = 'DropdownWithOnBrandSort',

  /* Report Export Page */
  REPORT_EXPORT_DOWNLOAD_STARTED = 'ReportExportDownloadStarted',
  REPORT_EXPORT_DOWNLOAD_FAILED = 'ReportExportDownloadFailed',
  REPORT_EXPORT_DOWNLOAD_SUCCEEDED = 'ReportExportDownloadSucceeded',

  /* Instagram Interactive Calendaar */
  BEST_TIME_TO_POST_SLOT_DROP = 'BestTimeToPostSlotDrop',
  MONTH_VIEW_ITEM_DRAG = 'MonthViewItemDrag',
  WEEK_VIEW_ITEM_DRAG = 'WeekViewItemDrag',

  /* Instagram Feed Preview */
  INSTAGRAM_FEED_PREVIEW_ITEM_DRAG = 'InstagramFeedPreviewItemDrag',

  /* Like2Buy Moderation Pinned Items */
  LIKE2BUY_PINNED_ITEM_DRAG = 'Like2buyPinnedItemDrag',

  /* Pinterest Post Details */
  REORDER_BULK_PIN_DRAG = 'ReorderBulkPinDrag',

  /**
   * These tracking actions were previously manually specified as the
   * insights-tracking-constants, hence the difference in casing.
   */
  CHANGE_ACTIVITY_REQUEST_TYPE = 'change-activity-request-type',
  CHANGE_ACTIVITY_VIEW = 'change-activity-view',
  CHANGE_ATTRIBUTION_MODEL = 'change-attribution-model',
  CHANGE_DATE_RANGE = 'change-date-range',
  CHANGE_PAGE_NUMBER = 'change-page-number',
  CHANGE_PAGE_SIZE = 'change-page-size',
  CHANGE_TABLE_SORT = 'change-table-sort',
  CLICK_EXPORT_EMAILS = 'click-export-emails',
  CLICK_EXPORT_ZIP = 'click-export-zip',
  CLICK_LEARN_MORE = 'click-learn-more',
  TOGGLE_DISPLAY_TYPE_LIST = 'toggle-display-type-list',

  /**
   * Insights V2
   */
  INSIGHTS_REFRESH_OPT_OUT = 'insights-refresh-opt-out',
  INSIGHTS_REFRESH_OPT_IN = 'insights-refresh-opt-in',

  /** Rights Configuration Modal */
  CREATE_MESSAGE = 'CreateMessage',
  CREATE_MESSAGE_CAMPAIGN = 'CreateMessageCampaign',
  DELETE_MESSAGE = 'DeleteMessage',
  DELETE_MESSAGE_CAMPAIGN = 'DeleteMessageCampaign',
  EDIT_MESSAGE = 'EditMessage',
  EDIT_MESSAGE_CAMPAIGN = 'EditMessageCampaign',
  VIEW_MESSAGE = 'ViewMessage',
  VIEW_MESSAGE_CAMPAGN = 'ViewMessageCampaign',

  /** Cross Posting */
  DEEP_LINK_IG_TO_FB = 'DeepLinkInstagramToFacebook',

  /**
   * Assorted additions to the enum, were previously just
   * passed in as raw strings throughout frontend code.
   */
  EMPLOYEE_ELEVATION_DISABLED = 'Employee:ElevationDisabled',
  EMPLOYEE_ELEVATION_ENABLED = 'Employee:ElevationEnabled',
  EMPLOYEE_VIEW_CHANGED_CLIENT_MODE = 'Employee:ViewChanged-CLIENT_MODE',
  EMPLOYEE_VIEW_CHANGED_DEV = 'Employee:ViewChanged-DEV',
  EMPLOYEE_VIEW_CHANGED_DOGFOOD = 'Employee:ViewChanged-DOGFOOD',
  EMPLOYEE_VIEW_CHANGED_GAMMA = 'Employee:ViewChanged-GAMMA',
  EMPLOYEE_VIEW_CHANGED_OPTINRELEASED = 'Employee:ViewChanged-OPTINRELEASED',
  EMPLOYEE_VIEW_CHANGED_RELEASED = 'Employee:ViewChanged-RELEASED',
  FACEBOOK_CONNECT_ACCOUNT = 'Facebook:ConnectAccount',
  FACEBOOK_DISCONNECT_ACCOUNT = 'Facebook:DisconnectAccount',
  GENERAL_PASSWORD_CHANGE = 'General:PasswordChange',
  GENERAL_UPDATE_TIMEZONE = 'General:UpdateTimezone',
  MODAL_CONFIGURE_MODAL = 'Modal.ConfigureModal',
  PINTEREST_DISCONNECT_ACCOUNT = 'Pinterest:DisconnectAccount',
  RESOURCES_KNOWLEDGE_BASE_CLICK = 'ResourcesKnowledgeBaseClick',
  RESOURCES_VIEW_GUIDE_CLICK = 'ResourcesViewGuideClick',
  RESOURCES_VIEW_WEBINARS_CLICK = 'ResourcesViewWebinarsClick',
  SORT_ASCENDING = 'SortAscending',
  SORT_DESCENDING = 'SortDescending',
  TWITTER_CONNECT_ACCOUNT = 'Twitter:ConnectAccount',
  TWITTER_DISCONNECT_ACCOUNT = 'Twitter:DisconnectAccount',
}

export enum TRACKING_LABEL {
  SUCCESS = 'success=true',
  FAIL = 'success=false',
  AUDIENCE = 'Audience',
  ENGAGEMENT = 'Engagement',
  SCHEDULE = 'Schedule',
  SUMMARY = 'Summary',
  TOUCHPOINTS_GALLERIES = '/galleries/website',
}

export interface IEventCategoryAndAction {
  eventCategory: TRACKING_CATEGORY;
  eventAction: TRACKING_ACTION;
}
